import React from 'react';
import styled from '@emotion/styled';
import { Project } from '../data/projects';
import { QRCodeSVG } from 'qrcode.react';

const PrintableContainer = styled.div`
  width: 210mm;  // A4 width
  min-height: 297mm;  // A4 height
  margin: 0 auto;
  background: white;
  padding: 15mm;  // Reduced from 20mm
  color: #000;
  font-family: 'Times New Roman', serif;
  position: relative;
  box-sizing: border-box;

  @media print {
    margin: 0;
    padding: 15mm;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
`;


const Header = styled.div`
  text-align: center;
  margin-bottom: 10mm;  // Reduced from 15mm
`;

const Title = styled.h1`
  font-size: 22pt;  // Reduced from 24pt
  font-weight: normal;
  margin: 0 0 3mm;  // Reduced from 4mm
  letter-spacing: 0.05em;
`;

const Subtitle = styled.h2`
  font-size: 13pt;  // Reduced from 14pt
  font-weight: normal;
  margin: 0;
  color: #666;
`;

const Content = styled.div`
  font-size: 10.5pt;  // Reduced from 11pt
  line-height: 1.5;  // Reduced from 1.6
  margin-bottom: 8mm;  // Reduced from 10mm
`;

const Section = styled.div`
  margin-bottom: 6mm;  // Reduced from 8mm
`;

const SectionTitle = styled.h3`
  font-size: 11pt;  // Reduced from 12pt
  font-weight: bold;
  margin: 0 0 3mm;  // Reduced from 4mm
  color: #333;
`;

const List = styled.ul`
  margin: 0;
  padding-left: 4mm;  // Reduced from 5mm
  
  li {
    margin-bottom: 1.5mm;  // Reduced from 2mm
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 4mm;  // Reduced from 6mm
  margin-bottom: 6mm;  // Reduced from 8mm
`;

const InfoBox = styled.div`
  border: 1px solid #ddd;
  padding: 3mm;  // Reduced from 4mm
`;

const InfoTitle = styled.h4`
  font-size: 10.5pt;  // Reduced from 11pt
  margin: 0 0 1.5mm;  // Reduced from 2mm
  color: #666;
`;

const InfoContent = styled.p`
  margin: 0;
  font-size: 10.5pt;  // Reduced from 11pt
`;

const Footer = styled.div`
  margin-top: 10mm;  // Reduced from 15mm
  text-align: center;
  font-size: 9pt;  // Reduced from 10pt
  color: #666;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4mm;  // Reduced from 6mm
`;

const Contact = styled.div`
  margin-top: 3mm;  // Reduced from 4mm
  font-style: italic;
`;

const QRCodeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5mm;  // Reduced from 2mm
  margin-top: 4mm;  // Reduced from 6mm
  
  canvas {
    border: 1px solid #eee;
    padding: 1.5mm;  // Reduced from 2mm
  }
`;

const QRLabel = styled.div`
  font-size: 8pt;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

interface Props {
  project: Project;
}

const ResidencyInvitation: React.FC<Props> = ({ project }) => {
  const projectUrl = `https://gallery.nothingtodo.me/projects/${project.id}`;

  return (
    <PrintableContainer>

      <Header>
        <Title>Artist Residency Program</Title>
        <Subtitle>March 2025 · Chiang Mai, Thailand</Subtitle>
      </Header>

      <Content>
        <Section>
          <p>
            We are pleased to invite applications for the Deja Artist Residency Program. 
            This program offers artists the opportunity to immerse themselves in their creative process 
            with full support in the vibrant creative environment of Chiang Mai.
          </p>
        </Section>

        <Grid>
          <InfoBox>
            <InfoTitle>Duration</InfoTitle>
            <InfoContent>{project.programDetails.duration}</InfoContent>
          </InfoBox>
          <InfoBox>
            <InfoTitle>Available Spots</InfoTitle>
            <InfoContent>{project.programDetails.spots.available} of {project.programDetails.spots.total} positions</InfoContent>
          </InfoBox>
          <InfoBox>
            <InfoTitle>Creative Grant</InfoTitle>
            <InfoContent>Up to 50,000 THB per artist</InfoContent>
          </InfoBox>
          <InfoBox>
            <InfoTitle>Revenue Share</InfoTitle>
            <InfoContent>50/50 split on artwork sales</InfoContent>
          </InfoBox>
        </Grid>

        <Section>
          <SectionTitle>Program Includes</SectionTitle>
          <List>
            {project.programDetails.facilities.map((facility, index) => (
              <li key={index}>{facility}</li>
            ))}
          </List>
        </Section>

        <Section>
          <SectionTitle>Requirements</SectionTitle>
          <List>
            {project.programDetails.requirements.map((requirement, index) => (
              <li key={index}>{requirement}</li>
            ))}
          </List>
        </Section>

        <Footer>
          <div>
            <p>For application and inquiries:</p>
            <Contact>
              Email: sheena.miyake@gmail.com
            </Contact>
          </div>
          
          <QRCodeContainer>
            <QRCodeSVG 
              value={projectUrl}
              size={80}
              level="H"
            />
            <QRLabel>Scan for more details</QRLabel>
          </QRCodeContainer>
        </Footer>
      </Content>
    </PrintableContainer>
  );
};

export default ResidencyInvitation; 